import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { OfferDiscountType, OfferItemRequest, OfferPreviewItem } from '../../types/offers';

export interface VariantPreviewRule {
  discountType: OfferDiscountType | null;
  maxDiscountPerBooking: number | null;
  /** Spec 073: each selected service with its own value (null = not set yet). */
  items: OfferItemRequest[];
}

export interface VariantPreviewState {
  /** Server rows, one item per selected service; null until a complete rule has been priced. */
  items: OfferPreviewItem[] | null;
  loading: boolean;
  error: boolean;
  /** False while there is no discount or no service to price. */
  ready: boolean;
}

const finite = (n: number | null): number | null => (n != null && Number.isFinite(n) && n > 0 ? n : null);

/**
 * Decision "4 preview": prices the draft rule per variant on the server (no client-side discount
 * maths), debounced so typing a value fires one request. Spec 073: each service is priced with its
 * own value (`items`); `discountValue` is sent as the lowest one, like the save request.
 */
export function useOfferVariantPreview(token: string | null | undefined, rule: VariantPreviewRule, delayMs = 400): VariantPreviewState {
  const items = rule.items.map((i) => ({ offeringId: i.offeringId, discountValue: finite(i.discountValue) }));
  const values = items.map((i) => i.discountValue).filter((v): v is number => v != null);
  const value = values.length ? Math.min(...values) : null;
  const cap = rule.discountType === 'PERCENT' ? finite(rule.maxDiscountPerBooking) : null;
  const ready = !!rule.discountType && value != null && items.length > 0;
  const key = JSON.stringify([rule.discountType, cap, items]);

  const [state, setState] = useState<Omit<VariantPreviewState, 'ready'>>({ items: null, loading: false, error: false });

  useEffect(() => {
    if (!token || !ready) {
      setState({ items: null, loading: false, error: false });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: false }));
    const timer = setTimeout(() => {
      api.previewOffer({
        discountType: rule.discountType,
        discountValue: value,
        maxDiscountPerBooking: cap,
        offeringIds: items.map((i) => i.offeringId),
        items,
      }, token)
        .then((res) => { if (!cancelled) setState({ items: res?.items ?? [], loading: false, error: false }); })
        .catch(() => { if (!cancelled) setState({ items: null, loading: false, error: true }); });
    }, delayMs);
    return () => { cancelled = true; clearTimeout(timer); };
    // `key` captures every input the request reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, key, delayMs]);

  return { ...state, ready };
}
